import { Injectable } from "@angular/core";
import { HttpService } from "./utils/http.service";
import { IEquipmentTrackingModel, EquipmentTrackingModel, DropdownModel, IDropdownModel, IDefaultEquipmentTrackingModel, IAutoCoverPreviousCoverModel } from "../models";
import { GlobalsService } from "./services.module";


interface EquipmentModelConfig {
    manufacturers: string[];
    modelsByManufacturer: Record<string, IDropdownModel[]>;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

@Injectable()
export class EquipmentTrackingService {

    async getDefaultEquipmentTrackings(): Promise<IDefaultEquipmentTrackingModel[]> {
        return await this.httpService.get("/equipmentTracking/getDefaultEquipmentTrackings");
    }

    async saveDefaultEquipmentTrackings(defaultModels: IDefaultEquipmentTrackingModel[]): Promise<IDefaultEquipmentTrackingModel[]> {
        return await this.httpService.post("/equipmentTracking/saveDefaultEquipmentTrackings", defaultModels);
    }

    // ---- Auto Cover tracker ----
    // Scoped to the single Auto Cover record. saveEquipmentTrackings() treats its payload as the
    // complete list for the job and deletes anything missing from it, so the wizard - which only
    // holds the one record - must never go through it.
    // structureId names the pool/spa the cover is on. A customer can have one on more than one pool, so
    // without it the server falls back to "the job's Auto Cover record", which is how this worked
    // before Assets.
    async getAutoCoverTracking(jobId: number, structureId: number = null): Promise<IEquipmentTrackingModel> {
        return await this.httpService.get(`/equipmentTracking/getAutoCoverTracking?jobId=${jobId}${this.structureParam(structureId)}`);
    }

    async saveAutoCoverTracking(jobId: number, equipmentTrackingModel: IEquipmentTrackingModel, jobDocumentKey: string, structureId: number = null): Promise<IEquipmentTrackingModel> {
        // Callers that don't already hold the job's document key send an empty guid (a missing value
        // would fail model binding); the server resolves it from the job in that case.
        const rootKey = jobDocumentKey || EMPTY_GUID;
        return await this.httpService.post(`/equipmentTracking/saveAutoCoverTracking?jobId=${jobId}&jobDocumentKey=${rootKey}${this.structureParam(structureId)}`, equipmentTrackingModel);
    }

    private structureParam(structureId: number): string {
        return (structureId === null || structureId === undefined) ? "" : `&structureId=${structureId}`;
    }

    async setAutoCoverLathamOrder(equipmentTrackingId: number, lathamOrderId: number): Promise<boolean> {
        return await this.httpService.post(`/equipmentTracking/setAutoCoverLathamOrder?equipmentTrackingId=${equipmentTrackingId}&lathamOrderId=${lathamOrderId}`, {});
    }

    // Whether the tracker has been saved from this job, for appointment types that require it
    async hasAutoCoverTracking(jobId: number): Promise<boolean> {
        return await this.httpService.get(`/equipmentTracking/hasAutoCoverTracking?jobId=${jobId}`);
    }

    // The covers recently ordered for the job's home owner, newest first, to check new measurements against
    async getPreviousCovers(jobId: number): Promise<IAutoCoverPreviousCoverModel[]> {
        return (await this.httpService.get(`/equipmentTracking/getPreviousCovers?jobId=${jobId}`)) || [];
    }

    equipmentConfig: Record<string, EquipmentModelConfig> = {

        /* ==============================
           POOL / SPA AUTOMATION
        ============================== */
        "Pool/Spa Automation": {
            manufacturers: ["Hayward", "Jandy", "Pentair"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("Intellicenter", "Intellicenter"),
                    new DropdownModel("Intellitouch", "Intellitouch"),
                    new DropdownModel("Easy Touch", "Easy Touch")
                ],
                "Hayward": [
                    new DropdownModel("Omnilogic", "Omnilogic")
                ],
                "Jandy": [
                    new DropdownModel("Jandy RS", "Jandy RS"),
                    new DropdownModel("Jandy Aqualink", "Jandy Aqualink"),
                    new DropdownModel("Purelink", "Purelink")
                ]
            }
        },

        /* ==============================
           HEATERS
        ============================== */
        "Heater": {
            manufacturers: ["Hayward", "Jandy", "Pentair", "Raypak"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("Heater", "Heater"),
                    new DropdownModel("Mastertemp", "Mastertemp"),
                    new DropdownModel("Max-E-Therm", "Max-E-Therm"),
                    new DropdownModel("Ultratemp", "Ultratemp"),
                    new DropdownModel("ETi", "ETi")
                ],
                "Hayward": [
                    new DropdownModel("Heatpro", "Heatpro"),
                    new DropdownModel("Universal H Series", "Universal H Series")
                ],
                "Raypak": [
                    new DropdownModel("Crosswind", "Crosswind")
                ],
                "Jandy": [
                    new DropdownModel("JXI", "JXI"),
                    new DropdownModel("JXIQ", "JXIQ"),
                    new DropdownModel("Versatemp", "Versatemp"),
                    new DropdownModel("Hi-E2", "Hi-E2")
                ]
            }
        },

        /* ==============================
           PUMPS
        ============================== */
        "Pump": {
            manufacturers: ["Aquastar", "Hayward", "Pentair", "Speck"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("IF3", "IF3")
                ],
                "Hayward": [
                    new DropdownModel("TriStar", "TriStar"),
                    new DropdownModel("Superpump", "Superpump"),
                    new DropdownModel("MaxFlo", "MaxFlo"),
                    new DropdownModel("PowerFlo", "PowerFlo"),
                    new DropdownModel("HCP3000", "HCP3000"),
                    new DropdownModel("VS FloPro", "VS FloPro")
                ],
                "Aquastar": [
                    new DropdownModel("PipeLine Pool Pump 1.5 HP", "PipeLine Pool Pump 1.5 HP"),
                    new DropdownModel("PipeLine Pool Pump 3 HP", "PipeLine Pool Pump 3 HP")
                ],
                "Speck": [
                    new DropdownModel("BADU Pro Series", "BADU Pro Series"),
                    new DropdownModel("Model 72", "Model 72"),
                    new DropdownModel("Model 433", "Model 433"),
                    new DropdownModel("Model 94 (Fountain Pump)", "Model 94 (Fountain Pump)"),
                    new DropdownModel("Model 95 (Plastic)", "Model 95 (Plastic)"),
                    new DropdownModel("Normblock", "Normblock"),
                    new DropdownModel("BADUJET Turbo Pro", "BADUJET Turbo Pro"),
                    new DropdownModel("Badustream II", "Badustream II"),
                    new DropdownModel("Badujet Super-Sport II", "Badujet Super-Sport II"),
                    new DropdownModel("BADUJET Imperial", "BADUJET Imperial")
                ]
            }
        },

        /* ==============================
           FILTERS
        ============================== */
        "Filter": {
            manufacturers: ["Aquastar", "Hayward", "Jandy", "Pentair"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("Clean and Clear", "Clean and Clear"),
                    new DropdownModel("Triton II", "Triton II")
                ],
                "Hayward": [
                    new DropdownModel("Swimclear", "Swimclear"),
                    new DropdownModel("Progrid DE", "Progrid DE"),
                    new DropdownModel("ProSeries", "ProSeries"),
                    new DropdownModel("Perflex", "Perflex"),
                    new DropdownModel("Xstream", "Xstream"),
                    new DropdownModel("StarClear", "StarClear")
                ],
                "Jandy": [
                    new DropdownModel("CV", "CV"),
                    new DropdownModel("CL", "CL"),
                    new DropdownModel("CS", "CS")
                ],
                "Aquastar": [
                    new DropdownModel("27000", "27000"),
                    new DropdownModel("35000", "35000")
                ]
            }
        },

        /* ==============================
           CHEMICAL AUTOMATION
        ============================== */
        "Chemical Automation": {
            manufacturers: ["Hayward", "Jandy", "Pentair"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("IntellipH", "IntellipH"),
                    new DropdownModel("Intellichem", "Intellichem")
                ],
                "Hayward": [
                    new DropdownModel("HL-CHEM", "HL-CHEM")
                ],
                "Jandy": [
                    new DropdownModel("TRUDOSE", "TRUDOSE"),
                    new DropdownModel("TRUSENSE", "TRUSENSE")
                ]
            }
        },

        /* ==============================
           LIGHTING
        ============================== */
        "Lighting": {
            manufacturers: ["Hayward", "PAL", "Pentair"],
            modelsByManufacturer: {
                "Pentair": [
                    new DropdownModel("IntelliVibe Nano Light", "IntelliVibe Nano Light"),
                    new DropdownModel("IntelliVibe Micro Light", "IntelliVibe Micro Light"),
                    new DropdownModel("IntelliVibe Mezzo Light", "IntelliVibe Mezzo Light"),
                    new DropdownModel("Microbrite", "Microbrite"),
                    new DropdownModel("Amerlite", "Amerlite"),
                    new DropdownModel("EasyTouch", "EasyTouch")
                ],
                "Hayward": [
                    new DropdownModel("ColorLogic", "ColorLogic"),
                    new DropdownModel("Guardian", "Guardian"),
                    new DropdownModel("Sollos", "Sollos"),
                    new DropdownModel("PureWhite", "PureWhite")
                ],
                "PAL": [
                    new DropdownModel("Treo Max", "Treo Max"),
                    new DropdownModel("Treo Mini", "Treo Mini"),
                    new DropdownModel("EvenGlow", "EvenGlow")
                ]
            }
        },

        /* ==============================
           SANITIZER
        ============================== */
        "Sanitizer": {
            manufacturers: ["Hayward", "Jandy", "Pentair"],
            modelsByManufacturer: {
                "Hayward": [
                    new DropdownModel("Tcells", "Tcells"),
                    new DropdownModel("Hydrapure", "Hydrapure"),
                    new DropdownModel("AQR3", "AQR3"),
                    new DropdownModel("HCXSCell", "HCXSCell")
                ]
            }
        }
    };
	constructor(private httpService: HttpService) { }

	// structureId scopes the call to one pool/spa on the Assets tab. The server treats the payload as
	// the complete list for whatever slice it names and deletes the rest, so it has to match the
	// structureId the list was loaded with - saving a pool's equipment while scoped to the whole job
	// would wipe out every other pool's equipment.
	async saveEquipmentTrackings(jobId: number, equipmentTrackings: IEquipmentTrackingModel[], jobDocumentKey: string, structureId: number = null): Promise<IEquipmentTrackingModel[]>{
		return await this.httpService.post(`/equipmentTracking/saveEquipmentTrackings?jobId=${jobId}&jobDocumentKey=${jobDocumentKey}${this.structureParam(structureId)}`, equipmentTrackings);
	}

	async addEquipmentTrackingByPhotoUpload(equipmentTrackingModel: IEquipmentTrackingModel, jobDocumentKey: string): Promise<IEquipmentTrackingModel> {
		return await this.httpService.post(`/equipmentTracking/AddEquipmentTrackingByPhotoUpload?jobDocumentKey=${jobDocumentKey}`, equipmentTrackingModel);
	}

    // structureId omitted returns everything on the job (how every caller worked before Assets);
    // a structure id returns that pool/spa's equipment, and 0 returns the "Unassigned" rows recorded
    // against this job before Assets existed.
    async getEquipmentTrackings(jobId: number, structureId: number = null): Promise<IEquipmentTrackingModel[]> {
        const equipmentTrackings =
            await this.httpService.get(`/equipmentTracking/getEquipmentTrackings?jobId=${jobId}${this.structureParam(structureId)}`);

        return this.applyOrendaConfigIfNeeded(equipmentTrackings);
    }

    public applyOrendaConfigIfNeeded(
        equipmentTrackings: IEquipmentTrackingModel[]
    ): IEquipmentTrackingModel[] {

        // Adjust this condition to however you determine Orenda
        const isOrenda = !!GlobalsService.company?.useOrenda;

        if (!isOrenda) {
            equipmentTrackings.forEach(x => x.showDropdown = false)
            return equipmentTrackings;
        }

        for (const equipment of equipmentTrackings) {
            this.applyEquipmentConfig(equipment);
        }

        return equipmentTrackings;
    }

   
    private applyEquipmentConfig(model: IEquipmentTrackingModel) {
        const config = this.equipmentConfig[model.equipmentName];
        model.showDropdown = true;

        if (!config) {
            model.manufacturerOptions = [];
            model.modelOptions = [];

            model.showDropdown = false;
            return;
        }

        // fake out the config
        if (config && model.manufacturer && !config.manufacturers.includes(model.manufacturer)) {
            model.otherManufacturer = model.manufacturer;
            model.manufacturer = "Other";
            model.showDropdown = false;
        }

        // Set manufacturer dropdown
        model.manufacturerOptions = config.manufacturers.map(x =>
            new DropdownModel(x, x)
        );

        model.manufacturerOptions.push(new DropdownModel("Other", "Other"));

        // Set model dropdown if manufacturer already selected
        if (model.manufacturer) {
            model.modelOptions =
                config.modelsByManufacturer[model.manufacturer] ?? [];
        } else {
            model.modelOptions = [];
        }
    }




}
